export type ForYouSubSectionId = 'needsAttention' | 'relevant' | 'comments'

export interface ForYouSubSectionState {
  hasVisibleItems: boolean
  hasLoadingItems: boolean
  visibleItemCount: number
}

export type ForYouSubSectionVisibility = Record<ForYouSubSectionId, ForYouSubSectionState>
export type ForYouSubSectionOverrides = Partial<Record<ForYouSubSectionId, boolean>>
export type ForYouSubSectionExpandedState = Record<ForYouSubSectionId, boolean>

const PRIORITY_ORDER: readonly ForYouSubSectionId[] = ['needsAttention', 'relevant', 'comments']

/**
 * Returns true once every subsection has settled (nothing loading) with no
 * confirmed visible items. Used to decide when to show the "Nothing for you to
 * act on now" empty state so the For You experience always appears while a PR is
 * open. While anything is still loading we return `false` so the empty state
 * does not flash before real content resolves.
 */
export function isForYouEmpty(visibility: ForYouSubSectionVisibility): boolean {
  if (PRIORITY_ORDER.some(id => visibility[id].hasLoadingItems)) return false
  return PRIORITY_ORDER.every(id => !visibility[id].hasVisibleItems)
}

/**
 * Picks the subsection to auto-expand once every subsection has finished loading.
 * We wait for everything to settle first, then expand the highest-priority
 * section that has content (Needs attention → Relevant). While
 * anything is still loading we return `null` so nothing expands mid-load and
 * then collapses when a higher-priority section resolves.
 */
export function pickExpandedSection(visibility: ForYouSubSectionVisibility): ForYouSubSectionId | null {
  if (PRIORITY_ORDER.some(id => visibility[id].hasLoadingItems)) return null
  return PRIORITY_ORDER.find(id => visibility[id].hasVisibleItems) ?? null
}

/**
 * Reconciles the priority recommendation with the user's manual toggles: an
 * explicit override always wins, otherwise the recommended section is expanded.
 */
export function getExpandedSubSections(
  visibility: ForYouSubSectionVisibility,
  overrides: ForYouSubSectionOverrides,
): ForYouSubSectionExpandedState {
  const recommended = pickExpandedSection(visibility)
  return {
    needsAttention: overrides.needsAttention ?? recommended === 'needsAttention',
    relevant: overrides.relevant ?? recommended === 'relevant',
    comments: overrides.comments ?? recommended === 'comments',
  }
}
